import { useEffect, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type TimeSeriesPoint } from "../lib/api";
import { ErrorState, FormSection } from "@/components/layout";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  days: number;
  interval: "hour" | "day";
  events: string[];
}

const EVENT_LABELS: Record<string, string> = {
  personalization_started: "Personalisierung gestartet",
  preview_generated: "Preview generiert",
  character_rated: "Character bewertet",
  add_to_cart: "In den Warenkorb",
  purchase: "Gekauft",
};

// Aus den Chart-Tokens in globals.css.
const COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-4)",
  "var(--chart-3)",
  "var(--chart-5)",
];

export default function EventsOverTimeChart({
  days,
  interval,
  events: eventTypes,
}: Props) {
  const [points, setPoints] = useState<TimeSeriesPoint[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setPoints(null);
    setError(null);
    api
      .eventsOverTime(interval, days)
      .then((res) => setPoints(res.points))
      .catch((e) => setError((e as Error).message));
  }, [days, interval, reloadKey]);

  // pivot points into { bucket, [event_a]: n, [event_b]: m, ... }
  const buckets = new Map<string, Record<string, number | string>>();
  for (const p of points ?? []) {
    const key = String(p.bucket);
    const row = buckets.get(key) ?? { bucket: key };
    row[p.event] = Number(p.count);
    buckets.set(key, row);
  }
  const data = Array.from(buckets.values());

  return (
    <FormSection title="Events im Zeitverlauf">
      {error ? (
        <ErrorState
          message={error}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : !points ? (
        <Skeleton className="h-[280px] w-full" />
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart
            data={data}
            margin={{ top: 8, right: 16, bottom: 0, left: -16 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--border)"
              vertical={false}
            />
            <XAxis
              dataKey="bucket"
              tickFormatter={(v) =>
                new Date(v).toLocaleDateString("de-DE", {
                  day: "2-digit",
                  month: "2-digit",
                })
              }
              fontSize={11}
              stroke="var(--muted-foreground)"
              tickLine={false}
            />
            <YAxis
              fontSize={11}
              allowDecimals={false}
              stroke="var(--muted-foreground)"
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              labelFormatter={(v) =>
                new Date(v as string).toLocaleString("de-DE")
              }
              formatter={(value: number, name: string) => [
                value,
                EVENT_LABELS[name] ?? name,
              ]}
              contentStyle={{
                borderRadius: 8,
                border: "1px solid var(--border)",
                fontSize: 12,
              }}
            />
            <Legend
              formatter={(name: string) => EVENT_LABELS[name] ?? name}
              wrapperStyle={{ fontSize: 12 }}
            />
            {eventTypes.map((ev, i) => (
              <Line
                key={ev}
                type="monotone"
                dataKey={ev}
                stroke={COLORS[i % COLORS.length]}
                dot={false}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </FormSection>
  );
}
